<template>
    <div class="voice-agent-page-content">
        <div class="header-section">
            <div class="title-wrapper">
                <h1 class="main-title">{{ $t("voiceagent.phoneNumbers") }}</h1>
                <p class="title-description">{{ $t("voiceagent.phoneNumbersDescription") }}</p>
            </div>
        </div>
        <div class="section">
            <div class="table-wrapper">
                <div class="toolbar">
                    <div class="left">
                        <a-input-search
                            style="width: 300px; margin: unset !important"
                            v-model="searchText"
                            placeholder="Ara"
                            class="search-input"
                            @input="onSearch"
                        />
                    </div>
                    <div class="right">
                        <PermissionController :permissions='["voice-agent.create"]'>
                            <a-dropdown :trigger="['click']">
                                <SupsisButton primary>
                                    <i class="material-icons">add</i>
                                    {{ $t("voiceagent.addNewNumber") }}
                                    <i class="material-icons"
                                       style="font-size: 16px; margin-left: 5px">arrow_drop_down</i>
                                </SupsisButton>
                                <a-menu slot="overlay" class="number-dropdown-menu">
                                    <a-menu-item @click="openNetGSM">
                                        <i class="material-icons">phone</i>
                                        {{ $t("voiceagent.buyNumberFromNetGSM") }}
                                    </a-menu-item>
                                    <a-menu-item @click="openVerimor">
                                        <i class="material-icons">phone</i>
                                        {{ $t("voiceagent.buyNumberFromVerimor") }}
                                    </a-menu-item>
                                    <a-menu-item @click="showRegistrationForm">
                                        <i class="material-icons">add_circle</i>
                                        {{ $t("voiceagent.createNumberRequest") }}
                                    </a-menu-item>
                                    <a-menu-item @click="showImportSipForm">
                                        <i class="material-icons">phone_forwarded</i>
                                        {{ $t("voiceagent.addNumberWithSipInfo") }}
                                    </a-menu-item>
                                </a-menu>
                            </a-dropdown>
                        </PermissionController>
                    </div>
                </div>

                <a-spin :spinning="loading">
                    <SupsisTable
                        :columns="getCols()"
                        :rows="paginatedPhoneNumbers"
                        :top-pagination="true"
                        empty-data-bordered
                        :bottom-pagination="false"
                        :pagination-cursor="pagination"
                        :options="{
                            rowStyleOption,
                            clipboardOption,
                            fixedHeader: true,
                            id: 'phone-numbers-table',
                            borderX: true,
                            borderY: true,
                        }"
                        @cursor-changed="(cursor) => (pagination = cursor)"
                    />
                </a-spin>
            </div>
        </div>

        <!-- Outbound Call Modal -->
        <a-modal
            v-model="showCallModal"
            :title="$t('voiceagent.makeCall')"
            :footer="null"
            @cancel="showCallModal = false"
            width="500px"
        >
            <a-form :form="callForm" layout="vertical" @submit="handleCallSubmit">
                <a-form-item :label="$t('voiceagent.numberToCall')">
                    <a-radio-group
                        v-model="callNumberFormat"
                        class="number-format-radio-group"
                        size="small"
                        button-style="solid"
                    >
                        <a-radio-button value="e164">{{ $t("voiceagent.formatToE164") }}</a-radio-button>
                        <a-radio-button value="raw">{{ $t("voiceagent.keepRawInput") }}</a-radio-button>
                    </a-radio-group>
                    <a-input
                        v-decorator="[
                            'to_number',
                            {
                                rules: [{ required: true, message: 'Lütfen aranacak numarayı girin!' }]
                            }
                        ]"
                        placeholder="+905xxxxxxxxx"
                    />
                    <div class="number-format-info">{{ $t("voiceagent.numberE164Format") }}</div>
                </a-form-item>

                <div class="button-container">
                    <a-button class="mr-2" @click="showCallModal = false">
                        {{ $t("voiceagent.cancel") }}
                    </a-button>
                    <a-button type="primary" html-type="submit" :loading="submittingCall">
                        {{ $t("voiceagent.call") }}
                    </a-button>
                </div>
            </a-form>
        </a-modal>

        <!-- Iframe Modal -->
        <a-modal
            v-model="showIframeModal"
            :title="$t('voiceagent.numberAdditionRequest')"
            :footer="null"
            @cancel="showIframeModal = false"
            width="800px"
            :destroyOnClose="true"
        >
            <iframe
                v-if="showIframeModal"
                src="https://form.supsis.ai/form/67f927997fe5efaf7e643a73/5d14ee322d95ad001ebdf213"
                style="width: 100%; height: 600px; border: none;"
            ></iframe>
        </a-modal>

        <!-- SIP Bilgileri ile Numara Ekle Modal -->
        <a-modal
            v-model="showSipImportModal"
            :title="$t('voiceagent.addNumberWithSipInfo')"
            :footer="null"
            @cancel="showSipImportModal = false"
            width="600px"
        >
            <a-spin :spinning="submittingSip">
                <a-form :form="sipImportForm" layout="vertical" @submit="handleSipImportSubmit">
                    <a-form-item :label="$t('voiceagent.phoneNumber')">
                        <a-radio-group
                            v-model="sipNumberFormat"
                            class="number-format-radio-group"
                            size="small"
                            button-style="solid"
                        >
                            <a-radio-button value="e164">{{ $t("voiceagent.formatToE164") }}</a-radio-button>
                            <a-radio-button value="raw">{{ $t("voiceagent.keepRawInput") }}</a-radio-button>
                        </a-radio-group>
                        <a-input
                            v-decorator="[
                                'phone_number',
                                {
                                    rules: [{ required: true, message: 'Lütfen telefon numarasını girin!' }]
                                }
                            ]"
                            placeholder="+905xxxxxxxxx"
                        />
                        <div class="number-format-info">{{ $t("voiceagent.e164FormatExample") }}</div>
                    </a-form-item>

                    <a-form-item :label="$t('voiceagent.sipTrunkUri')">
                        <a-input
                            v-decorator="[
                                'termination_uri',
                                {
                                    rules: [{ required: true, message: 'Lütfen SIP Trunk URI bilgisini girin!' }]
                                }
                            ]"
                            placeholder="ornek.pstn.twilio.com"
                        />
                        <div class="number-format-info">{{ $t("voiceagent.sipTrunkUriInfo") }}</div>
                    </a-form-item>

                    <a-form-item :label="$t('voiceagent.sipTrunkUsername')">
                        <a-input
                            v-decorator="[
                                'sip_trunk_auth_username',
                                {}
                            ]"
                            placeholder="Kullanıcı adı (isteğe bağlı)"
                        />
                    </a-form-item>

                    <a-form-item :label="$t('voiceagent.sipTrunkPassword')">
                        <a-input-password
                            v-decorator="[
                                'sip_trunk_auth_password',
                                {}
                            ]"
                            placeholder="Şifre (isteğe bağlı)"
                        />
                    </a-form-item>

                    <a-form-item :label="$t('voiceagent.nickname')">
                        <a-input
                            v-decorator="[
                                'nickname',
                                {}
                            ]"
                            placeholder="Numara takma adı (isteğe bağlı)"
                        />
                    </a-form-item>


                    <div class="button-container">
                        <a-button class="mr-2" @click="showSipImportModal = false">
                            {{ $t("voiceagent.cancel") }}
                        </a-button>
                        <a-button type="primary" html-type="submit" :loading="submittingSip">
                            {{ $t("voiceagent.add") }}
                        </a-button>
                    </div>
                </a-form>
            </a-spin>
        </a-modal>
    </div>
</template>

<script>
import { VOICE_AGENT_API } from "../../../../../api/voiceAgent";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import _ from "lodash";
import moment from "moment";

export default {
    name: "VoiceAgentPhoneNumbers",
    components: {
        SupsisTable,
        SupsisButton,
    },
    props: {
        channelId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            searchText: "",
            phoneNumbers: [],
            agents: [],
            loading: false,
            submittingCall: false,
            submittingSip: false,
            pagination: {
                page: 1,
                perPage: 10,
                total: 0,
            },
            rowStyleOption: {
                stripe: true,
            },
            clipboardOption: {
                copy: true,
                paste: false,
                cut: false,
                delete: false,
            },
            showCallModal: false,
            showIframeModal: false,
            showSipImportModal: false,
            callForm: this.$form.createForm(this),
            sipImportForm: this.$form.createForm(this),
            selectedPhoneNumber: null,
            callNumberFormat: "e164",
            sipNumberFormat: "e164",
        };
    },
    computed: {
        filteredPhoneNumbers() {
            if (!this.searchText) {
                return this.phoneNumbers;
            }
            const searchLower = this.searchText.toLowerCase();
            return this.phoneNumbers.filter((number) => {
                const phoneNumber = _.get(number, "phone_number", "").toLowerCase();
                const nickname = _.get(number, "nickname", "").toLowerCase();
                return phoneNumber.includes(searchLower) || nickname.includes(searchLower);
            });
        },
        paginatedPhoneNumbers() {
            const start = (this.pagination.page - 1) * this.pagination.perPage;
            const end = start + this.pagination.perPage;
            return this.filteredPhoneNumbers.slice(start, end);
        },
        agentOptions() {
            return this.agents.map(agent => ({
                value: agent.agent_id,
                label: agent.agent_name,
            }));
        },
    },
    methods: {
        async fetchPhoneNumbers() {
            try {
                this.loading = true;
                const phoneNumbers = await VOICE_AGENT_API.numbers.list();
                this.phoneNumbers = phoneNumbers;
                this.pagination.total = phoneNumbers.length;
            } catch (error) {
                console.error("Telefon numaraları çekilirken hata oluştu:", error);
                this.$message.error("Telefon numaraları yüklenirken bir hata oluştu");
            } finally {
                this.loading = false;
            }
        },
        async fetchAgents() {
            try {
                const agents = await VOICE_AGENT_API.agents.list();
                this.agents = agents;
            } catch (error) {
                console.error("Ajanlar çekilirken hata oluştu:", error);
                this.$message.error("Ajanlar yüklenirken bir hata oluştu");
            }
        },
        onSearch() {
            this.pagination.page = 1;
        },
        getCols() {
            return [
                {
                    key: "phone_number",
                    field: "phone_number",
                    title: "Numara",
                    renderBodyCell: ({ row }) => {
                        return (
                            <span>{row.phone_number_pretty || row.phone_number}</span>
                        );
                    },
                },
                {
                    key: "type",
                    field: "phone_number_type",
                    title: "Tip",
                    renderBodyCell: ({ row }) => {
                        let type = row.phone_number_type || "unknown";
                        if (type === "retell-twilio") return "Twilio";
                        if (type === "retell-telnyx") return "Telnyx";
                        if (type === "custom") return "Özel";
                        return type;
                    },
                },
                {
                    key: "nickname",
                    field: "nickname",
                    title: "Takma Ad",
                    renderBodyCell: ({ row }) => {
                        return row.nickname || "-";
                    },
                },
                {
                    key: "inbound_agent",
                    field: "inbound_agent_id",
                    title: "Gelen Ajan",
                    renderBodyCell: ({ row }) => {
                        return (
                            <a-select
                                value={row.inbound_agent_id || "none"}
                                style="width: 200px"
                                placeholder="Ajan seçin"
                                onChange={(value) => {
                                    const inboundAgentId = value === "none" ? null : value;
                                    this.updatePhoneNumber(row.phone_number, {
                                        inbound_agent_id: inboundAgentId,
                                        outbound_agent_id: row.outbound_agent_id || null,
                                    });
                                }}
                            >
                                <a-select-option value="none">Ajan yok</a-select-option>
                                {this.agentOptions.map(agent => (
                                    <a-select-option key={agent.value} value={agent.value}>
                                        {agent.label}
                                    </a-select-option>
                                ))}
                            </a-select>
                        );
                    },
                },
                {
                    key: "outbound_agent",
                    field: "outbound_agent_id",
                    title: "Giden Ajan",
                    renderBodyCell: ({ row }) => {
                        return (
                            <a-select
                                value={row.outbound_agent_id || "none"}
                                style="width: 200px"
                                placeholder="Ajan seçin"
                                onChange={(value) => {
                                    const outboundAgentId = value === "none" ? null : value;
                                    this.updatePhoneNumber(row.phone_number, {
                                        outbound_agent_id: outboundAgentId,
                                        inbound_agent_id: row.inbound_agent_id || null,
                                    });
                                }}
                            >
                                <a-select-option value="none">Ajan yok</a-select-option>
                                {this.agentOptions.map(agent => (
                                    <a-select-option key={agent.value} value={agent.value}>
                                        {agent.label}
                                    </a-select-option>
                                ))}
                            </a-select>
                        );
                    },
                },
                {
                    key: "area_code",
                    field: "area_code",
                    title: "Alan Kodu",
                    renderBodyCell: ({ row }) => {
                        return row.area_code || "-";
                    },
                },
                {
                    key: "last_modified",
                    field: "last_modification_timestamp",
                    title: "Son Değişiklik",
                    renderBodyCell: ({ row }) => {
                        if (!row.last_modification_timestamp) return "-";
                        const date = new Date(row.last_modification_timestamp);
                        return moment(date).format("DD.MM.YYYY HH:mm");
                    },
                },
                {
                    key: "actions",
                    field: "actions",
                    title: "İşlemler",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="actions">
                                <SupsisButton
                                    class="call-btn"
                                    onClick={() => this.makeCall(row)}
                                    ghost
                                    icon="call"
                                    small
                                />
                                <SupsisButton
                                    onClick={() => this.deletePhoneNumber(row.phone_number)}
                                    danger
                                    ghost
                                    icon="delete"
                                    small
                                />
                            </div>
                        );
                    },
                },
            ];
        },
        async updatePhoneNumber(phoneNumber, data) {
            try {
                this.loading = true;
                await VOICE_AGENT_API.numbers.update(phoneNumber, data);

                // Numarayı güncelledikten sonra listeyi yenile
                await this.fetchPhoneNumbers();

                this.$message.success("Telefon numarası başarıyla güncellendi");
            } catch (error) {
                console.error("Telefon numarası güncellenirken hata oluştu:", error);
                this.$message.error("Telefon numarası güncellenirken bir hata oluştu");
            } finally {
                this.loading = false;
            }
        },
        async deletePhoneNumber(phoneNumber) {
            try {
                await this.$confirm({
                    title: "Telefon Numarasını Sil",
                    content: "Bu telefon numarasını silmek istediğinize emin misiniz?",
                    okText: "Evet",
                    okType: "danger",
                    cancelText: "Hayır",
                    onOk: async () => {
                        this.loading = true;
                        await VOICE_AGENT_API.numbers.delete(phoneNumber);
                        this.phoneNumbers = this.phoneNumbers.filter(
                            (num) => num.phone_number !== phoneNumber,
                        );
                        this.pagination.total = this.phoneNumbers.length;
                        this.loading = false;
                        this.$message.success("Telefon numarası başarıyla silindi");
                    },
                });
            } catch (error) {
                console.error("Telefon numarası silinirken hata oluştu:", error);
                this.$message.error("Telefon numarası silinirken bir hata oluştu");
            }
        },
        makeCall(phoneNumber) {
            this.selectedPhoneNumber = phoneNumber;
            this.showCallModal = true;
            this.callNumberFormat = "e164";
            this.callForm.resetFields();
        },
        formatToE164(number) {
            if (!number) return number;
            const digits = number.replace(/\D/g, "");
            if (number.startsWith("+")) {
                return "+" + digits;
            }
            if (digits.startsWith("0")) {
                return "+9" + digits;
            }
            return "+" + digits;
        },
        handleCallSubmit(e) {
            e.preventDefault();
            this.callForm.validateFields(async (err, values) => {
                if (!err && this.selectedPhoneNumber) {
                    this.submittingCall = true;

                    try {
                        const toNumber = this.callNumberFormat === "e164"
                            ? this.formatToE164(values.to_number)
                            : values.to_number;

                        const callData = {
                            fromNumber: this.selectedPhoneNumber.phone_number,
                            toNumber: toNumber,
                        };

                        // Arama isteği gönder
                        await VOICE_AGENT_API.calls.createPhone(callData);

                        this.$message.success("Arama başarıyla başlatıldı");
                        this.showCallModal = false;
                    } catch (error) {
                        console.error("Arama başlatılırken hata oluştu:", error);
                        this.$message.error("Arama başlatılırken bir hata oluştu");
                    } finally {
                        this.submittingCall = false;
                    }
                }
            });
        },
        openNetGSM() {
            window.open("https://abonelik.netgsm.com.tr/bayilik/abonelik/2958/asama1.php?referer=bilgilendirme&kullaniciislem=sth", "_blank");
        },
        openVerimor() {
            window.open("https://oim.verimor.com.tr/abonelik-basvurusu", "_blank");
        },
        showRegistrationForm() {
            this.showIframeModal = true;
        },
        showImportSipForm() {
            this.showSipImportModal = true;
            this.sipNumberFormat = "e164";
            this.sipImportForm.resetFields();
        },
        handleSipImportSubmit(e) {
            e.preventDefault();
            this.sipImportForm.validateFields(async (err, values) => {
                if (!err) {
                    this.submittingSip = true;

                    try {
                        const phoneNumber = this.sipNumberFormat === "e164"
                            ? this.formatToE164(values.phone_number)
                            : values.phone_number;

                        const importData = {
                            phoneNumber: phoneNumber,
                            terminationUri: values.termination_uri,
                        };

                        // Opsiyonel alanları ekle
                        if (values.sip_trunk_auth_username) {
                            importData.sipTrunkAuthUsername = values.sip_trunk_auth_username;
                        }

                        if (values.sip_trunk_auth_password) {
                            importData.sipTrunkAuthPassword = values.sip_trunk_auth_password;
                        }

                        if (values.nickname) {
                            importData.nickname = values.nickname;
                        }

                        // if (values.inbound_webhook_url) {
                        //     importData.inboundWebhookUrl = values.inbound_webhook_url;
                        // }

                        // Numara import işlemini yap
                        await VOICE_AGENT_API.numbers.import(importData);

                        // Başarılı mesajı göster
                        this.$message.success("Telefon numarası başarıyla eklendi");

                        // Modal'ı kapat
                        this.showSipImportModal = false;

                        // Numara listesini yenile
                        await this.fetchPhoneNumbers();
                    } catch (error) {
                        console.error("Telefon numarası eklenirken hata oluştu:", error);
                        this.$message.error("Telefon numarası eklenirken bir hata oluştu");
                    } finally {
                        this.submittingSip = false;
                    }
                }
            });
        },
    },
    created() {
        this.fetchPhoneNumbers();
        this.fetchAgents();
    },
    watch: {
        pagination: {
            handler() {
                // Sayfa değişikliğinde yapılacak işlemler
            },
            deep: true,
        },
    },
};
</script>

<style scoped lang="scss">
.voice-agent-page-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 0 20px;

    .header-section {
        margin-bottom: 5px;
    }

    .title-wrapper {
        display: flex;
        align-items: center;
        gap: 15px;
        margin-bottom: 20px;
    }

    .main-title {
        font-size: 24px;
        font-weight: 500;
        margin: 0;
    }

    .title-description {
        font-size: 14px;
        font-weight: 400;
        color: #8c8c8c;
        margin: 0;
    }

    .section {
        display: flex;
        flex-direction: column;
        gap: 15px;
    }

    .table-wrapper {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .toolbar {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;
            gap: 5px;

            .left {
                display: flex;
                gap: 5px;
                align-items: center;
            }

            .right {
                display: flex;
                gap: 5px;
            }
        }
    }

    #phone-numbers-table {
        .actions {
            display: flex;
            gap: 5px;
        }
    }

    .button-container {
        display: flex;
        justify-content: flex-end;
        margin-top: 20px;
    }

    .number-format-info {
        font-size: 12px;
        color: #8c8c8c;
        margin-top: 4px;
    }

    .number-format-radio-group {
        display: flex;
        flex-wrap: nowrap;
        width: 100%;
        margin-bottom: 8px;

        ::v-deep .ant-radio-button-wrapper {
            flex: 1;
            text-align: center;
            height: 32px;
            line-height: 30px;
            font-size: 12px;
            font-weight: 500;
            border-radius: 0;
        }

        ::v-deep .ant-radio-button-wrapper:first-child {
            border-radius: 6px 0 0 6px;
        }

        ::v-deep .ant-radio-button-wrapper:last-child {
            border-radius: 0 6px 6px 0;
        }

        ::v-deep .ant-radio-button-wrapper:not(:first-child)::before {
            display: none;
        }
    }
}

.number-dropdown-menu {
    .ant-dropdown-menu-item {
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 14px;
    }
}
</style>
